import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './styled';

const NUMBER_OF_IMAGES = 5;
const IMAGE_GAP = 10;
const IMAGE_WIDTH = 161;

const AVAILABLE_SLIDER_LANGUAGES = ['en', 'fr', 'de', 'it', 'pt', 'uk', 'es'];

export const ImagesSlider = () => {
  const { i18n } = useTranslation();
  const sliderRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartScroll = useRef(0);
  const isScrolling = useRef(false);
  const lastSwipeTime = useRef(0);

  const imageLang = useMemo(() => {
    const raw = (i18n.language || 'en').toLowerCase();
    const base = raw.split('-')[0];
    console.log('Determined image language:', raw, base);
    if (AVAILABLE_SLIDER_LANGUAGES.includes(raw)) return raw;
    if (AVAILABLE_SLIDER_LANGUAGES.includes(base)) return base;
    return 'en';
  }, [i18n.language]);

  const scrollDistance = IMAGE_WIDTH + IMAGE_GAP;

  const getCurrentImageIndex = () => {
    if (!sliderRef.current) return 0;
    const scrollLeft = sliderRef.current.scrollLeft;
    const padding = 16;
    return Math.round((scrollLeft + padding) / scrollDistance);
  };

  const scrollToImage = (targetIndex) => {
    if (!sliderRef.current || isScrolling.current) return;

    const container = sliderRef.current;
    const padding = 16;
    const targetScroll = Math.max(
      0,
      Math.min(
        targetIndex * scrollDistance - padding,
        container.scrollWidth - container.clientWidth
      )
    );

    isScrolling.current = true;

    container.style.scrollSnapType = 'none';

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });

    setTimeout(() => {
      container.style.scrollSnapType = '';
      isScrolling.current = false;
    }, 400);
  };

  const handleTouchStart = (e) => {
    if (!sliderRef.current) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartScroll.current = sliderRef.current.scrollLeft;
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) return;

    const now = Date.now();
    if (now - lastSwipeTime.current < 500) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    const minSwipeDistance = 30;

    const scrollDiff = Math.abs(
      touchStartScroll.current - (sliderRef.current?.scrollLeft || 0)
    );
    if (scrollDiff > 5) {
      return;
    }

    if (Math.abs(diff) > minSwipeDistance) {
      lastSwipeTime.current = now;
      const currentIndex = getCurrentImageIndex();

      if (diff > 0 && currentIndex < NUMBER_OF_IMAGES - 1) {
        scrollToImage(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        scrollToImage(currentIndex - 1);
      }
    }
  };

  const handleMouseDown = (e) => {
    if (!sliderRef.current) return;
    touchStartX.current = e.clientX;
    touchStartScroll.current = sliderRef.current.scrollLeft;
  };

  const handleMouseUp = (e) => {
    if (isScrolling.current) return;

    const now = Date.now();
    if (now - lastSwipeTime.current < 500) return;

    const mouseEndX = e.clientX;
    const diff = touchStartX.current - mouseEndX;
    const minSwipeDistance = 30;

    const scrollDiff = Math.abs(
      touchStartScroll.current - (sliderRef.current?.scrollLeft || 0)
    );
    if (scrollDiff > 5) {
      return;
    }

    if (Math.abs(diff) > minSwipeDistance) {
      lastSwipeTime.current = now;
      const currentIndex = getCurrentImageIndex();

      if (diff > 0 && currentIndex < NUMBER_OF_IMAGES - 1) {
        scrollToImage(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        scrollToImage(currentIndex - 1);
      }
    }
  };

  const imagePaths = Array.from(
    { length: NUMBER_OF_IMAGES },
    (_, i) => `./assets/storeV2/slider/${imageLang}/${i + 1}.png`
  );

  return (
    <S.ImagesSliderContainer
      ref={sliderRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {imagePaths.map((path, index) => (
        <S.SliderImage
          key={index}
          src={path}
          alt={`Slider image ${index + 1}`}
          $gap={IMAGE_GAP}
        />
      ))}
    </S.ImagesSliderContainer>
  );
};
